// ─────────────────────────────────────────────────────────────
// JamieChatView — chat interface for the AI Product Owner.
// Renders markdown responses streamed via SSE. When Jamie proposes
// creating a card, the user is prompted to confirm before the card
// is created.
// ─────────────────────────────────────────────────────────────

import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Avatar,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AddCommentIcon from "@mui/icons-material/AddComment";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";
import type { ChatMessage, JamieChatAction, UpstreamError } from "../types";
import { jamieApi, cardsApi } from "../api/client";
import { MarkdownChatBody } from "../components/MarkdownChatBody";
import { UpstreamErrorBubble } from "../components/UpstreamErrorBubble";

// Static assets in `public/` are served under Vite's `base` (e.g. "/" locally,
// "/discoverstudio/" in production). Using `import.meta.env.BASE_URL` keeps the
// avatar paths correct in both environments — `BASE_URL` always ends with "/".
const ASSET_BASE = import.meta.env.BASE_URL;
const JAMIE_AVATAR_SRC = `${ASSET_BASE}avatars/jamie.png`;

interface DisplayMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** True while this message is still being streamed */
  streaming?: boolean;
  action?: JamieChatAction | null;
  createdAt: Date;
  /**
   * Structured upstream-provider error payload (Sam, 2026-05-14).
   * When present, MessageBubble renders an <UpstreamErrorBubble>
   * in place of the normal chat bubble, naming the model + provider
   * and offering category-specific recovery advice. Set when the SSE
   * stream emits a `{"type": "error", "error": <UpstreamError>}` frame.
   */
  upstreamError?: UpstreamError;
}

const QUICK_PROMPTS = [
  "What should we prioritize next?",
  "I have a feature idea",
  "Help me write a card for better error reporting",
  "What patterns do you see in the backlog?",
];

const ENTERPRISE_MODE_KEY = "msdrad";

const STANDARD_GREETING =
  "Hi, I'm Jamie — the Product Owner for Maps Discover Studio. I'm here to help you:\n- Submit and refine feature requests\n- Report bugs\n- Explore what's planned and what's been built\n- Shape the direction of the product\n\nWhat's on your mind?";

const ENTERPRISE_GREETING = STANDARD_GREETING;

function detectMode(): string | null {
  const params = new URLSearchParams(window.location.search);
  const val = params.get("jamie");
  return val === ENTERPRISE_MODE_KEY ? "enterprise" : null;
}

function storageKey(mode: string | null): string {
  return mode === "enterprise" ? "jamie_chat_history_enterprise" : "jamie_chat_history_normal";
}

function defaultMessages(mode: string | null): DisplayMessage[] {
  return [
    {
      id: "welcome",
      role: "assistant",
      content: mode === "enterprise" ? ENTERPRISE_GREETING : STANDARD_GREETING,
      action: null,
      createdAt: new Date(),
    },
  ];
}

const STALE_SESSION_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

/** Deserialize messages from localStorage, falling back to the default greeting. */
function loadMessages(mode: string | null): DisplayMessage[] {
  try {
    const raw = localStorage.getItem(storageKey(mode));
    if (!raw) return defaultMessages(mode);
    const parsed = JSON.parse(raw) as Array<{
      id: string;
      role: "user" | "assistant";
      content: string;
      action?: JamieChatAction | null;
      createdAt: string;
      upstreamError?: UpstreamError;
    }>;
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultMessages(mode);
    const messages = parsed.map((m) => ({ ...m, action: m.action ?? null, createdAt: new Date(m.createdAt) }));
    // Auto-reset if the newest message is older than 1 week
    const newest = messages.reduce((a, b) => (a.createdAt > b.createdAt ? a : b));
    if (Date.now() - newest.createdAt.getTime() > STALE_SESSION_MS) {
      localStorage.removeItem(storageKey(mode));
      return defaultMessages(mode);
    }
    return messages;
  } catch {
    return defaultMessages(mode);
  }
}

/** Persist completed (non-streaming) messages to localStorage. */
function saveMessages(mode: string | null, messages: DisplayMessage[]): void {
  try {
    const toSave = messages.filter((m) => !m.streaming);
    localStorage.setItem(storageKey(mode), JSON.stringify(toSave));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded) — silently ignore.
  }
}

export function JamieChatView() {
  const [mode] = useState<string | null>(() => detectMode());

  const [messages, setMessages] = useState<DisplayMessage[]>(() => loadMessages(detectMode()));
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId] = useState(() => uuidv4());
  const [pendingAction, setPendingAction] = useState<{
    msgId: string;
    action: JamieChatAction;
  } | null>(null);
  const [confirmingCard, setConfirmingCard] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Persist completed messages to localStorage whenever the list changes.
  useEffect(() => {
    saveMessages(mode, messages);
  }, [mode, messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const chatHistory = useCallback((): ChatMessage[] => {
    return messages
      .filter((m) => m.id !== "welcome")
      .map((m) => ({ role: m.role, content: m.content }));
  }, [messages]);

  // Keep a ref to the current streaming message ID so callbacks can target it.
  const streamingMsgIdRef = useRef<string | null>(null);
  // Keep an AbortController ref so we can cancel on unmount.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cancel any in-progress stream when the component unmounts.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;

      setError(null);

      const userMsg: DisplayMessage = {
        id: uuidv4(),
        role: "user",
        content: trimmed,
        action: null,
        createdAt: new Date(),
      };

      // Insert user message and a blank streaming assistant message.
      const assistantMsgId = uuidv4();
      const assistantMsg: DisplayMessage = {
        id: assistantMsgId,
        role: "assistant",
        content: "",
        streaming: true,
        action: null,
        createdAt: new Date(),
      };

      streamingMsgIdRef.current = assistantMsgId;
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setInput("");
      setSending(true);

      // Closure flag: when `onUpstreamError` fires it finalizes the
      // streaming placeholder with a structured <UpstreamErrorBubble>.
      // The SSE client ALSO invokes `onError` immediately after (legacy
      // contract — see jamieApi.chatStream). We flip this flag so that
      // legacy handler doesn't strip the bubble we just rendered or
      // surface a redundant top-of-chat Alert.
      let upstreamErrorHandled = false;

      const controller = jamieApi.chatStream(
        {
          message: trimmed,
          history: chatHistory(),
          ...(mode !== null && { mode }),
        },
        {
          onToken: (token) => {
            const targetId = streamingMsgIdRef.current;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === targetId ? { ...m, content: m.content + token } : m
              )
            );
          },
          onDone: (action, cleanText) => {
            const targetId = streamingMsgIdRef.current;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === targetId
                  ? {
                      ...m,
                      content: cleanText || m.content,
                      streaming: false,
                      action: action ?? null,
                    }
                  : m
              )
            );
            if (action?.action === "create_card" && targetId) {
              setPendingAction({ msgId: targetId, action });
            }
            streamingMsgIdRef.current = null;
            setSending(false);
          },
          onUpstreamError: (upstream) => {
            // Structured upstream-provider failure. Finalize the
            // streaming placeholder in place — <MessageBubble> below
            // detects `upstreamError` and renders a soft warning
            // surface naming the model + provider. Keeps the failed
            // turn anchored in the chat timeline rather than
            // disappearing into a top-of-chat banner.
            upstreamErrorHandled = true;
            const targetId = streamingMsgIdRef.current;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === targetId
                  ? {
                      ...m,
                      content: "",
                      streaming: false,
                      upstreamError: upstream,
                    }
                  : m,
              ),
            );
            streamingMsgIdRef.current = null;
            setSending(false);
          },
          onError: (message) => {
            // Structured upstream errors are already finalized into
            // a bubble by onUpstreamError — don't double-handle.
            if (upstreamErrorHandled) return;
            // Legacy bare-string error (auth/config). Remove the
            // blank/partial assistant message and surface via Alert.
            const targetId = streamingMsgIdRef.current;
            setMessages((prev) => prev.filter((m) => m.id !== targetId));
            setError(message);
            streamingMsgIdRef.current = null;
            setSending(false);
          },
        }
      );

      abortControllerRef.current = controller;
    },
    [sending, chatHistory, mode]
  );

  const handleConfirmCard = useCallback(async () => {
    if (!pendingAction) return;
    setConfirmingCard(true);
    try {
      await cardsApi.create(pendingAction.action.card);
      // Inject a confirmation message
      setMessages((prev) => [
        ...prev,
        {
          id: uuidv4(),
          role: "assistant",
          content: `Card created: **"${pendingAction.action.card.title}"** has been added to the backlog.`,
          action: null,
          createdAt: new Date(),
        },
      ]);
      setPendingAction(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create card");
    } finally {
      setConfirmingCard(false);
    }
  }, [pendingAction]);

  const handleDismissCard = useCallback(() => {
    setPendingAction(null);
    setMessages((prev) => [
      ...prev,
      {
        id: uuidv4(),
        role: "assistant",
        content: "No problem — I won't create that card. Let me know if you'd like to adjust the idea first.",
        action: null,
        createdAt: new Date(),
      },
    ]);
  }, []);

  const handleClearConversation = () => {
    localStorage.removeItem(storageKey(mode));
    setMessages([
      {
        id: "welcome",
        role: "assistant",
        content:
          mode === "enterprise" ? ENTERPRISE_GREETING : STANDARD_GREETING,
        action: null,
        createdAt: new Date(),
      },
    ]);
    setPendingAction(null);
    setError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: 800,
        mx: "auto",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Avatar
          src={JAMIE_AVATAR_SRC}
          alt="Jamie"
          sx={{ width: 32, height: 32 }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: "1rem" }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">Jamie</Typography>
          <Typography variant="caption" color="text.secondary">
            AI Product Owner — ideas, refinement, card creation
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCommentIcon fontSize="small" />}
          onClick={handleClearConversation}
          aria-label="Start new chat"
        >
          New chat
        </Button>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isPendingAction={pendingAction?.msgId === msg.id}
            onConfirmCard={handleConfirmCard}
            onDismissCard={handleDismissCard}
            confirmingCard={confirmingCard}
          />
        ))}

        {/* Show a waiting indicator only while we're connected but no tokens have arrived yet */}
        {sending && messages[messages.length - 1]?.content === "" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={JAMIE_AVATAR_SRC}
              alt="Jamie"
              sx={{ width: 24, height: 24 }}
            >
              <SmartToyOutlinedIcon sx={{ fontSize: "0.875rem" }} />
            </Avatar>
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <CircularProgress size={10} />
              <Typography variant="caption" color="text.secondary">
                Jamie is thinking…
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div ref={bottomRef} />
      </Box>

      <Divider />

      {/* Quick prompts — shown only when conversation is fresh */}
      {messages.length <= 2 && (
        <Box sx={{ px: 3, pt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {QUICK_PROMPTS.map((prompt) => (
            <Chip
              key={prompt}
              label={prompt}
              size="small"
              variant="outlined"
              onClick={() => void sendMessage(prompt)}
              sx={{
                cursor: "pointer",
                fontSize: "0.75rem",
                "&:hover": { borderColor: "primary.main", color: "primary.main" },
              }}
            />
          ))}
        </Box>
      )}

      {/* Input area */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
        }}
      >
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Jamie anything about the product… (Enter to send, Shift+Enter for newline)"
          multiline
          maxRows={6}
          fullWidth
          disabled={sending}
          size="small"
        />
        <Tooltip title="Send message (Enter)">
          <span>
            <IconButton
              color="primary"
              onClick={() => void sendMessage(input)}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              sx={{ flexShrink: 0 }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── MessageBubble ─────────────────────────────────────────────

interface MessageBubbleProps {
  message: DisplayMessage;
  isPendingAction: boolean;
  onConfirmCard: () => void;
  onDismissCard: () => void;
  confirmingCard: boolean;
}

function MessageBubble({
  message,
  isPendingAction,
  onConfirmCard,
  onDismissCard,
  confirmingCard,
}: MessageBubbleProps) {
  const isUser = message.role === "user";
  const isStreaming = !isUser && message.streaming === true;

  // ── Structured upstream-error bubble (Sam, 2026-05-14) ─────────
  // SSE emitted `{"type": "error", "error": <UpstreamError>}` — a
  // transient AI-provider failure (overload, rate-limit, timeout,
  // mid-stream truncation, etc.). Render a soft warning surface in
  // place of Jamie's normal bubble; keeps the failed turn anchored
  // in the chat timeline and names the model + provider so it reads
  // as upstream, not a Maps Discover Studio bug.
  if (message.upstreamError) {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexDirection: "row",
          alignItems: "flex-start",
        }}
      >
        <Avatar
          src={JAMIE_AVATAR_SRC}
          alt="Jamie"
          sx={{ width: 32, height: 32, flexShrink: 0 }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: "1rem" }} />
        </Avatar>
        <Box sx={{ maxWidth: "80%", minWidth: 0 }}>
          <UpstreamErrorBubble error={message.upstreamError} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, px: 0.5, textAlign: "left" }}
          >
            {message.createdAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        flexDirection: isUser ? "row-reverse" : "row",
        alignItems: "flex-start",
      }}
    >
      {/* Avatar */}
      {isUser ? (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: "1rem" }} />
        </Box>
      ) : (
        <Avatar
          src={JAMIE_AVATAR_SRC}
          alt="Jamie"
          sx={{ width: 32, height: 32, flexShrink: 0 }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: "1rem" }} />
        </Avatar>
      )}

      {/* Bubble */}
      <Box sx={{ maxWidth: "80%", minWidth: 0 }}>
        <Paper
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: isUser ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
            backgroundColor: isUser ? "primary.main" : "action.selected",
            color: isUser ? "primary.contrastText" : "text.primary",
          }}
        >
          {isUser ? (
            // User messages render plain — we don't want their
            // literal asterisks turning into bold runs.
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}
            >
              {message.content}
            </Typography>
          ) : (
            // Jamie streams markdown source (bullets, bold, etc.).
            // Render through the shared MarkdownChatBody so the
            // formatting actually applies. Cursor color follows
            // the bubble's contrast text so the bar stays visible
            // on Jamie's tinted background in light + dark themes.
            <MarkdownChatBody
              content={message.content}
              isStreaming={isStreaming}
              cursorColor="currentColor"
            />
          )}
        </Paper>

        {/* Card creation confirmation widget */}
        {isPendingAction && message.action?.action === "create_card" && (
          <Paper
            sx={{
              mt: 1,
              p: 1.5,
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: "4px",
              backgroundColor: "action.selected",
            }}
          >
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
              Jamie wants to create a card:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {message.action.card.title}
            </Typography>
            {message.action.card.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {message.action.card.description}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  confirmingCard ? <CircularProgress size={10} /> : <CheckIcon />
                }
                onClick={onConfirmCard}
                disabled={confirmingCard}
              >
                {confirmingCard ? "Creating…" : "Create card"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon />}
                onClick={onDismissCard}
                disabled={confirmingCard}
              >
                Not now
              </Button>
            </Box>
          </Paper>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5, px: 0.5, textAlign: isUser ? "right" : "left" }}
        >
          {message.createdAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </Typography>
      </Box>
    </Box>
  );
}
